"use client";

import { type CSSProperties, type JSX, useMemo } from "react";

import { useHydrated } from "@/hooks/use-hydrated";
import { clamp, cn } from "@/lib/utils";

import { CalendarContext, type CalendarContextValue } from "./calendar-context";
import { CalendarSkeleton } from "./components/calendar-skeleton";
import { CalendarToolbar } from "./components/calendar-toolbar";
import { MonthView } from "./components/month-view";
import { WeekView } from "./components/week-view";
import { buildEventIndex } from "./event-layout";
import { useControllableState } from "./hooks/use-controllable-state";
import {
  type CalendarEvent,
  type CalendarProps,
  type CalendarRange,
} from "./types";
import {
  addToView,
  formatPeriodTitle,
  getVisibleRange,
  sanitizeHiddenWeekdays,
} from "./utils";

// =============================================================================

// Stable fallbacks so omitted props never invalidate the memos
const NO_EVENTS: CalendarEvent[] = [];
const NO_RANGES: CalendarRange[] = [];
const NO_WEEKDAYS: number[] = [];

/**
 * Week (time grid) and month calendar, purely visual: it renders the events
 * it receives and reports the clicks, the booking rules stay with the caller.
 * View and date are controlled or uncontrolled (`view` / `defaultView` /
 * `onViewChange`, same for `date`). Every user-facing string comes from
 * `labels`, every date label from `locale`. Dates are read in the browser's
 * local time: the grid is drawn once hydrated.
 *
 * Height: the week view scrolls internally under the root's max height,
 * `min(44rem, 85dvh)` by default, overridden through `className` (e.g.
 * `max-h-[40rem]` or a fixed `h-[36rem]`). The month view always grows with
 * its content: it drops those height bounds.
 *
 * @example
 * <Calendar
 *   locale={fr}
 *   labels={labels}
 *   events={bookings}
 *   hiddenWeekdays={[0]}
 *   businessHours={{ startHour: 9, endHour: 18 }}
 *   disabledRanges={closedRanges}
 *   onSlotClick={({ start, end }) => openBooking(start, end)}
 * />
 */
export function Calendar<TData = unknown>(
  props: CalendarProps<TData>
): JSX.Element {
  const {
    events = NO_EVENTS,
    view: viewProp,
    defaultView = "week",
    onViewChange,
    date: dateProp,
    defaultDate,
    onDateChange,
    locale,
    labels,
    weekStartsOn = 1,
    hiddenWeekdays = NO_WEEKDAYS,
    minHour: minHourProp = 8,
    maxHour: maxHourProp = 20,
    slotDuration = 30,
    hourHeight = 56,
    businessHours,
    disabledRanges = NO_RANGES,
    onSlotClick,
    onEventClick,
    onDateClick,
    renderEventDetail,
    renderDayAddon,
    isDayDisabled,
    headerActions,
    tone = "ink",
    className,
  } = props;

  const isHydrated = useHydrated();
  const [view, setView] = useControllableState(
    viewProp,
    defaultView,
    onViewChange
  );
  const [date, setDate] = useControllableState(
    dateProp,
    () => defaultDate ?? new Date(),
    onDateChange
  );

  // Keyed by value: inline arrays and objects must not churn the memos below
  const hiddenKey = hiddenWeekdays.join(",");
  const hidden = useMemo(
    () =>
      sanitizeHiddenWeekdays(hiddenKey ? hiddenKey.split(",").map(Number) : []),
    [hiddenKey]
  );
  const businessStart = businessHours?.startHour;
  const businessEnd = businessHours?.endHour;
  const resolvedBusinessHours = useMemo(
    () =>
      businessStart === undefined || businessEnd === undefined
        ? undefined
        : { startHour: businessStart, endHour: businessEnd },
    [businessStart, businessEnd]
  );

  const minHour = clamp(Math.floor(minHourProp), 0, 23);
  const maxHour = clamp(Math.ceil(maxHourProp), minHour + 1, 24);

  // Only the events on screen are indexed
  const range = getVisibleRange(view, date, weekStartsOn);
  const rangeStart = range.start.getTime();
  const rangeEnd = range.end.getTime();
  const eventIndex = useMemo(
    () =>
      buildEventIndex(events, {
        start: new Date(rangeStart),
        end: new Date(rangeEnd),
      }),
    [events, rangeStart, rangeEnd]
  );

  // The payload type is erased for the internals, which hand back the very
  // objects they received
  const context: CalendarContextValue = {
    tone,
    date,
    setDate,
    locale,
    labels,
    weekStartsOn,
    hiddenWeekdays: hidden,
    minHour,
    maxHour,
    slotDuration,
    businessHours: resolvedBusinessHours,
    disabledRanges,
    onSlotClick,
    onEventClick: onEventClick as CalendarContextValue["onEventClick"],
    onDateClick,
    renderEventDetail:
      renderEventDetail as CalendarContextValue["renderEventDetail"],
    renderDayAddon,
    isDayDisabled,
  };

  return (
    <CalendarContext value={context}>
      <div
        className={cn(
          "flex max-h-[min(44rem,85dvh)] min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-line bg-background text-foreground",
          className,
          // The month grows with its content whatever the week is bound to
          view === "month" && "h-auto max-h-none"
        )}
        style={
          {
            "--calendar-hour-height": `${hourHeight / 16}rem`,
          } as CSSProperties
        }
      >
        <CalendarToolbar
          view={view}
          title={
            isHydrated
              ? formatPeriodTitle(view, date, locale, weekStartsOn, hidden)
              : null
          }
          onViewChange={setView}
          onPrevious={() => setDate(addToView(date, view, -1))}
          onToday={() => setDate(new Date())}
          onNext={() => setDate(addToView(date, view, 1))}
          headerActions={headerActions}
        />
        <div className="flex min-h-0 flex-1 flex-col">
          {!isHydrated ? (
            <CalendarSkeleton
              view={view}
              hourCount={maxHour - minHour}
              hasDayAddon={!!renderDayAddon}
            />
          ) : view === "week" ? (
            <WeekView eventIndex={eventIndex} />
          ) : (
            <MonthView eventIndex={eventIndex} />
          )}
        </div>
      </div>
    </CalendarContext>
  );
}
